// src/features/streams/components/simulcast/FacebookEditForm.tsx

import { Stack, TextInput, Group, Button } from '@mantine/core';
import { useForm, useController } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { SIMULCAST_CONSTRAINTS } from '@/lib/constants/simulcast';
import type { ISimulcastAllData } from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { fbEditFormSchema, type FBEditFormData } from '../../schemas/SimulcastForm.schema';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';
import { BroadcastMetadataOverrideSection } from './BroadcastMetadataOverrideSection';

interface FacebookEditFormProps {
  simulcast: ISimulcastAllData;
  stream: StreamViewData;
  onSubmit: (data: FBEditFormData) => void;
  onCancel: () => void;
  isPending: boolean;
}

export function FacebookEditForm({
  simulcast,
  stream,
  onSubmit,
  onCancel,
  isPending,
}: FacebookEditFormProps) {
  const defaultValues = SimulcastTransforms.transformFBToEditFormData(simulcast);

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<FBEditFormData>({
    resolver: zodResolver(fbEditFormSchema),
    defaultValues,
  });

  const { field: labelField } = useController({ name: 'label', control });

  // Show location as read-only (defensive access for fb sub-object)
  const locationLabel = simulcast.fb?.fbLocationLabel ?? 'Facebook';

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack gap="md">
        <TextInput
          label="Location"
          value={locationLabel}
          readOnly
          variant="filled"
          description="Location cannot be changed after creation"
        />

        <TextInput
          label="Destination Name"
          description="A name to identify this destination in your dashboard"
          value={labelField.value}
          onChange={labelField.onChange}
          onBlur={labelField.onBlur}
          error={errors.label?.message}
          maxLength={SIMULCAST_CONSTRAINTS.LABEL_MAX}
        />

        <BroadcastMetadataOverrideSection
          streamTitle={stream.media.title}
          streamDescription={stream.media.descShort ?? ''}
          control={control}
          errors={errors}
          existingTitle={simulcast.core.title ?? undefined}
          existingDescription={simulcast.core.descShort ?? undefined}
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending} disabled={!isDirty}>
            Save Changes
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
